import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useCreateDealer, useDealers, useDeleteDealer } from '../api/hooks';
import { offerDeactivateOrForceDelete } from '../utils/historyAwareDelete';
import { MAX_VIDEO_SECONDS } from '../components/investor/documentRules';
import PendingDocumentPicker from '../components/investor/PendingDocumentPicker';
import PasswordConfirmModal from '../components/common/PasswordConfirmModal';
import { usePasswordConfirm } from '../utils/confirmWithPassword';
import type { DealerListItem, DocumentType } from '../types';
import { apiDate, date, money } from '../utils/format';
import { paymentMethodOptions } from '../utils/paymentMethods';

const DEAL_TYPE_OPTIONS = [
  { value: 'PercentageBased', label: 'Monthly % based — profit accrues daily from a rate' },
  { value: 'FixedReturn', label: 'Fixed investment — a set amount comes back' },
];

const MOBILE_PATTERN = /^03\d{2}-?\d{7}$/;
const CNIC_PATTERN = /^\d{5}-?\d{7}-?\d$/;

/** The Dealers list — mirrors the Investors page exactly: search, table, add, view, delete. */
export default function DealersPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useDealers(search || undefined);
  const deleteDealer = useDeleteDealer();
  const passwordConfirm = usePasswordConfirm();
  const qc = useQueryClient();

  const onDelete = async (row: DealerListItem) => {
    if (!(await passwordConfirm.confirm(`delete ${row.dealerCode} — ${row.fullName}`))) return;

    Modal.confirm({
      title: `Delete ${row.dealerCode} — ${row.fullName}?`,
      content:
        'This only succeeds if the dealer has no transaction history. If they have any investments, withdrawals or profit payments, you will be offered the choice to deactivate instead.',
      okText: 'Delete permanently',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDealer.mutateAsync({ id: row.id });
          message.success('Dealer deleted.');
        } catch (err) {
          if (errorMessage(err).includes('has transaction history')) {
            offerDeactivateOrForceDelete({
              code: row.dealerCode,
              fullName: row.fullName,
              onDeactivate: async () => {
                await api.patch(`/dealers/${row.id}/status`, { status: 'Inactive' });
                qc.invalidateQueries({ queryKey: ['dealers'] });
              },
              onForceDelete: () => deleteDealer.mutateAsync({ id: row.id, force: true }),
              deactivatedMessage: 'Dealer deactivated.',
              deletedMessage: 'Dealer and all transaction history deleted.',
            });
          } else {
            Modal.error({ title: 'Could not delete this dealer', content: errorMessage(err) });
          }
        }
      },
    });
  };

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Dealers
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Add dealer
        </Button>
      </Space>

      <Card>
        <Input.Search
          allowClear
          placeholder="Search by name, mobile, CNIC or code"
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={setSearch}
        />

        <Table<DealerListItem>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          scroll={{ x: 'max-content' }}
          onRow={(row) => ({ onClick: () => navigate(`/dealers/${row.id}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Code', dataIndex: 'dealerCode' },
            { title: 'Name', dataIndex: 'fullName' },
            { title: 'Mobile', dataIndex: 'mobile', render: (v: string | null) => v ?? '—' },
            {
              title: 'Type',
              dataIndex: 'dealType',
              render: (v: string) => (
                <Tag color={v === 'FixedReturn' ? 'geekblue' : 'gold'}>{v === 'FixedReturn' ? 'Fixed' : '% Based'}</Tag>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (v: string) => <Tag color={v === 'Active' ? 'green' : 'default'}>{v}</Tag>,
            },
            { title: 'Total investment', dataIndex: 'currentPrincipal', align: 'right', render: money },
            {
              title: 'Outstanding profit',
              dataIndex: 'outstandingProfit',
              align: 'right',
              render: (v: number) => <span style={{ color: v > 0 ? '#cf1322' : undefined }}>{money(v)}</span>,
            },
            {
              title: 'Next due date',
              render: (_, row) =>
                !row.nextExpectedReturn ? (
                  '—'
                ) : (
                  <Space direction="vertical" size={0}>
                    <span>{date(row.nextExpectedReturn)}</span>
                    <Tag color={row.hasOverdue ? 'red' : row.daysToNextReturn <= 7 ? 'orange' : 'blue'}>
                      {row.daysToNextReturn < 0
                        ? `${Math.abs(row.daysToNextReturn)} days overdue`
                        : `${row.daysToNextReturn} days left`}
                    </Tag>
                  </Space>
                ),
            },
            {
              title: 'Actions',
              render: (_, row) => (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" onClick={() => navigate(`/dealers/${row.id}`)}>
                    View
                  </Button>
                  <Button danger type="link" size="small" onClick={() => onDelete(row)}>
                    Delete
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <CreateDealerModal open={open} onClose={() => setOpen(false)} />
      <PasswordConfirmModal state={passwordConfirm.state} />
    </>
  );
}

/**
 * Mirrors CreateInvestorModal field for field: identity, payment details, then documents, then an
 * optional initial investment. Documents can only be attached once the dealer row exists, so — same
 * as investors — they are uploaded right after creation, and a failed upload does not undo it.
 */
function CreateDealerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const createDealer = useCreateDealer();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<File | null>(null);
  const [cnicImage, setCnicImage] = useState<File | null>(null);
  const [agreementVideo, setAgreementVideo] = useState<File | null>(null);
  const [agreementImage, setAgreementImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const dealType = Form.useWatch('dealType', form) as string | undefined;
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const resetAll = () => {
    form.resetFields();
    setPhoto(null);
    setCnicImage(null);
    setAgreementVideo(null);
    setAgreementImage(null);
  };

  const uploadPending = async (dealerId: number): Promise<string[]> => {
    const pending: Array<[DocumentType, File | null]> = [
      ['photo', photo],
      ['cnic-image', cnicImage],
      ['agreement-video', agreementVideo],
      ['agreement-image', agreementImage],
    ];

    const failed: string[] = [];
    for (const [type, file] of pending) {
      if (!file) continue;
      try {
        const body = new FormData();
        body.append('file', file);
        await api.post(`/dealers/${dealerId}/documents/${type}`, body, {
          headers: { 'Content-Type': undefined },
        });
      } catch {
        failed.push(type);
      }
    }
    return failed;
  };

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      const result = await createDealer.mutateAsync({
        ...values,
        initialPlacementDate: apiDate(values.initialPlacementDate as dayjs.Dayjs | undefined),
        initialExpectedReturnDate: apiDate(values.initialExpectedReturnDate as dayjs.Dayjs | undefined),
        returnDate: apiDate(values.returnDate as dayjs.Dayjs | undefined),
      });

      setUploading(true);
      const failedUploads = await uploadPending(result.dealer.id);
      setUploading(false);
      if (failedUploads.length > 0) {
        message.warning(
          `Dealer created, but these files did not upload: ${failedUploads.join(', ')}. Add them from the profile tab.`,
        );
      }

      // The generated password is shown once and never returned again — same rule as investors.
      Modal.success({
        title: 'Dealer created',
        width: 480,
        content: (
          <div>
            <p>
              Code / username: <strong>{result.username}</strong>
            </p>
            <p>
              Temporary password: <strong>{result.generatedPassword}</strong>
            </p>
            <p style={{ color: '#cf1322' }}>Save this password now — it will not be shown again.</p>
          </div>
        ),
      });
      resetAll();
      onClose();
      navigate(`/dealers/${result.dealer.id}`);
    } catch (err) {
      setUploading(false);
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal
      title="Add dealer"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={createDealer.isPending || uploading}
      okText="Create"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
        <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="fatherName" label="Father's name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile"
          label="Mobile"
          rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Enter an 11-digit mobile number' }]}
        >
          <Input placeholder="03001234567" />
        </Form.Item>
        <Form.Item
          name="cnic"
          label="CNIC"
          rules={[{ required: true }, { pattern: CNIC_PATTERN, message: 'Enter a 13-digit CNIC number' }]}
          help="Cannot be changed after the account is created."
        >
          <Input placeholder="3520212345671" />
        </Form.Item>
        <Form.Item name="address" label="Address" rules={[{ required: true }]} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="paymentMethod" label="Payment method" rules={[{ required: true }]}>
          <Select
            placeholder="Select how money moves to and from this investor"
            options={paymentMethodOptions}
          />
        </Form.Item>
        {paymentMethod !== 'Cash' && (
          <Form.Item name="accountNumber" label="Account / wallet number" rules={[{ required: true }]}>
            <Input placeholder="Account title, IBAN or wallet number" />
          </Form.Item>
        )}

        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          Documents
        </Divider>
        <PendingDocumentPicker
          type="photo"
          label="Profile photo"
          hint="JPG, PNG or WebP · max 5 MB"
          file={photo}
          onChange={setPhoto}
        />
        <PendingDocumentPicker
          type="cnic-image"
          label="CNIC image"
          hint="JPG, PNG or WebP · max 5 MB"
          file={cnicImage}
          onChange={setCnicImage}
        />
        <PendingDocumentPicker
          type="agreement-video"
          label="Agreement video"
          hint={`MP4 or WebM · max ${MAX_VIDEO_SECONDS} seconds · 25 MB`}
          file={agreementVideo}
          onChange={setAgreementVideo}
        />
        <PendingDocumentPicker
          type="agreement-image"
          label="Agreement image"
          hint="JPG, PNG or WebP · max 5 MB"
          file={agreementImage}
          onChange={setAgreementImage}
        />

        <Divider orientation="left" plain>
          Initial investment
        </Divider>
        <Form.Item
          name="dealType"
          label="Investment type"
          rules={[{ required: true }]}
          initialValue="PercentageBased"
          style={{ marginTop: 8 }}
        >
          <Select options={DEAL_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="initialAmount" label="Amount" rules={[{ required: true }]}>
          <InputNumber min={0.01} style={{ width: '100%' }} />
        </Form.Item>

        {dealType === 'FixedReturn' ? (
          <>
            <Form.Item
              name="returnAmount"
              label="Fixed return amount (PKR)"
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, value: number) =>
                    value === undefined || value === null || value >= (getFieldValue('initialAmount') ?? 0)
                      ? Promise.resolve()
                      : Promise.reject(new Error('The return cannot be less than the amount invested.')),
                }),
              ]}
              help="The whole sum coming back, capital included. No monthly rate applies."
            >
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="returnDate"
              label="Return due on"
              rules={[{ required: true }]}
              help="Becomes the default date for any further investments on this account."
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            name="initialProfitRate"
            label="Monthly profit rate (%)"
            help="Optional. Leave blank if the rate is not agreed yet — no profit accrues until it is set."
          >
            <InputNumber min={0} max={99.99} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="initialPlacementDate" label="Investment date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        {dealType !== 'FixedReturn' && (
          <Form.Item name="initialExpectedReturnDate" label="Profit expected on" help="Used for the countdown on the dealer's page.">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Optional notes about this account" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
